import { SectionLabel } from "@risalabs_frontend_org/oasis-ui-kit";
import type { CoverageServiceCode } from "../../../../data-model/coverageInformation";

/** Props for {@link EvCoverageMedicaidServiceTable}. */
interface EvCoverageMedicaidServiceTableProps {
  title: string;
  services: CoverageServiceCode[];
}

/**
 * Read-only Code / Description table for a Medicaid service list.
 *
 * @param props - Section title and the service-code rows to render
 * @returns A bordered two-column table, or an empty-state row when no services
 */
const EvCoverageMedicaidServiceTable = ({
  title,
  services,
}: EvCoverageMedicaidServiceTableProps) => (
  <div className="flex min-w-0 flex-1 flex-col">
    <SectionLabel
      label={title}
      accentColor="#0056D6"
      className="mb-2 !text-tiny !font-bold !leading-tiny !tracking-[0.03125rem] !text-primaryGray-6 sm:mb-3"
    />
    <div className="flex flex-col self-stretch overflow-hidden rounded-md border border-primaryGray-15">
      <div className="grid h-11 grid-cols-[minmax(4rem,1fr)_2fr] items-center self-stretch border-b border-primaryGray-15 bg-primaryGray-16">
        <div className="px-3 text-tiny font-bold leading-tiny text-primaryGray-2">
          Code
        </div>
        <div className="px-3 text-tiny font-bold leading-tiny text-primaryGray-2">
          Description
        </div>
      </div>
      {services.length === 0 ? (
        <div className="flex h-16 items-center px-3 text-tiny font-normal leading-tiny tracking-[0.0125rem] text-primaryGray-3">
          No services found.
        </div>
      ) : (
        services.map((service, index) => (
          <div
            key={`${service.code}-${index}`}
            className={`grid h-16 grid-cols-[minmax(4rem,1fr)_2fr] items-center self-stretch ${
              index < services.length - 1
                ? "border-b border-primaryGray-15"
                : ""
            }`}
          >
            <div className="px-3 text-tiny font-normal leading-tiny tracking-[0.0125rem] text-primaryGray-1">
              {service.code}
            </div>
            <div className="px-3 text-tiny font-normal leading-tiny tracking-[0.0125rem] text-primaryGray-1">
              {service.description}
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

/** Props for {@link EvCoverageMedicaidServices}. */
export interface EvCoverageMedicaidServicesProps {
  coveredServices: CoverageServiceCode[];
  nonCoveredServices: CoverageServiceCode[];
}

/**
 * Treatment tab for Medicaid coverage: side-by-side Covered Services and Non
 * Covered Services tables sourced from `medicaid_nycsc_info`.
 *
 * @param props - Covered and non-covered service-code rows
 * @returns The Treatment tab body
 */
export const EvCoverageMedicaidServices = ({
  coveredServices,
  nonCoveredServices,
}: EvCoverageMedicaidServicesProps) => (
  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
    <EvCoverageMedicaidServiceTable
      title="Covered Services"
      services={coveredServices}
    />
    <EvCoverageMedicaidServiceTable
      title="Non Covered Services"
      services={nonCoveredServices}
    />
  </div>
);

export default EvCoverageMedicaidServices;
